import { useEffect, useState } from 'react';
import {
  Divider,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Grid,
  Chip,
} from '@mui/material';
import { Info, Mail, Calendar, RefreshCw, X, Bell, Check, Video } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { NotificationT, SEVERITY } from '../../types/notification';
import Notifications from '../../components/common/Notifications';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useEmailSettings } from '../../hooks/email-settings/useEmailSettings';

const EmailSettings = () => {
  const { emailSettings, isLoading, error } = useSelector(
    (state: RootState) => state.emailSettings
  );
  const { selectedOrganizationId, refetch, saveEmailSettings, updateEmailSettings } =
    useEmailSettings();

  const { role } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      isEmailEnabled: emailSettings?.isEmailEnabled || false,
      isScheduleEmailEnabled: emailSettings?.isScheduleEmailEnabled || false,
      isRescheduleEmailEnabled: emailSettings?.isRescheduleEmailEnabled || false,
      isCancelEmailEnabled: emailSettings?.isCancelEmailEnabled || false,
      isReminderEmailEnabled: emailSettings?.isReminderEmailEnabled || false,
    },
  });

  // Update form values when emailSettings changes
  useEffect(() => {
    if (emailSettings) {
      setValue('isEmailEnabled', emailSettings?.isEmailEnabled || false);
      setValue('isScheduleEmailEnabled', emailSettings?.isScheduleEmailEnabled || false);
      setValue('isRescheduleEmailEnabled', emailSettings?.isRescheduleEmailEnabled || false);
      setValue('isCancelEmailEnabled', emailSettings?.isCancelEmailEnabled || false);
      setValue('isReminderEmailEnabled', emailSettings?.isReminderEmailEnabled || false);
    }
  }, [emailSettings, setValue]);

  const handleSaveOrUpdate = async (data: any) => {
    if (!selectedOrganizationId) return;

    // Validate that at least one Email type is enabled if isEmailEnabled is true
    if (
      data.isEmailEnabled &&
      !data.isScheduleEmailEnabled &&
      !data.isRescheduleEmailEnabled &&
      !data.isCancelEmailEnabled &&
      !data.isReminderEmailEnabled
    ) {
      setNotification({
        open: true,
        message: 'At least one Email type must be enabled when Email notifications are enabled.',
        severity: SEVERITY.ERROR,
      });
      return;
    }

    setIsUpdating(true);
    try {
      if (emailSettings) {
        await updateEmailSettings(data);
        setNotification({
          open: true,
          message: 'Email settings updated successfully',
          severity: SEVERITY.SUCCESS,
        });
      } else {
        await saveEmailSettings(data);
        setNotification({
          open: true,
          message: 'Email settings created successfully',
          severity: SEVERITY.SUCCESS,
        });
      }
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save Email settings';
      setNotification({
        open: true,
        message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsUpdating(false);
      refetch();
    }
  };

  useEffect(() => {
    // Initial fetch
    refetch();
  }, [refetch]);

  if (!selectedOrganizationId) {
    return (
      <Alert
        severity="info"
        icon={<Info />}
        sx={{
          borderRadius: theme => theme.shape.borderRadius || '8px',
          boxShadow: theme => theme.shadows[1],
          fontWeight: 500,
        }}
      >
        Please select an organization to view Email settings.
      </Alert>
    );
  }

  if (isLoading || isUpdating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" icon={<X />}>
        {error}
      </Alert>
    );
  }

  const EmailTypeCard = ({
    title,
    icon,
    enabled,
  }: {
    title: string;
    icon: React.ReactNode;
    enabled: boolean;
  }) => {
    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: '8px',
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {icon}
              <Typography variant="h6" ml={1}>
                {title}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={enabled ? 'Enabled' : 'Disabled'}
              color={enabled ? 'success' : 'default'}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {role !== AUTH_CONSTANTS.ROLES.USER && !isEditing && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            onClick={() => setIsEditing(true)}
            variant="contained"
            disableElevation
            size="small"
            startIcon={<RefreshCw size={16} />}
            sx={{
              borderRadius: theme => theme.shape.borderRadius || '8px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Configure
          </Button>
        </Box>
      )}

      {isEditing ? (
        <form onSubmit={handleSubmit(handleSaveOrUpdate)}>
          <Card
            sx={{
              mb: 4,
              borderRadius: theme => theme.shape.borderRadius || '8px',
              border: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Global Settings
              </Typography>
              <Box mb={2}>
                <Controller
                  name="isEmailEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          color="primary"
                          onChange={e => {
                            const checked = e.target.checked;
                            field.onChange(checked);

                            // If toggled, also update all email type settings
                            setValue('isScheduleEmailEnabled', checked);
                            setValue('isRescheduleEmailEnabled', checked);
                            setValue('isCancelEmailEnabled', checked);
                            setValue('isReminderEmailEnabled', checked);
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" fontWeight={500}>
                          Enable Email Notifications
                        </Typography>
                      }
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Calendar size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      Schedule
                    </Typography>
                  </Box>
                  <Controller
                    name="isScheduleEmailEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Enable Schedule Emails"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <RefreshCw size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      Reschedule
                    </Typography>
                  </Box>
                  <Controller
                    name="isRescheduleEmailEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Enable Reschedule Emails"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <X size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      Cancellation
                    </Typography>
                  </Box>
                  <Controller
                    name="isCancelEmailEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Enable Cancellation Emails"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Bell size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      Reminder
                    </Typography>
                  </Box>
                  <Controller
                    name="isReminderEmailEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} color="primary" />}
                        label="Enable Reminder Emails"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card
            sx={{
              mt: 4,
              borderRadius: '8px',
              border: theme => `1px solid ${theme.palette.divider}`,
              backgroundColor: theme => theme.palette.action.hover,
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={1.5}>
                <Info size={18} style={{ marginRight: '8px' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Available Template Variables
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Use these variables in your email templates. They will be automatically replaced
                with actual values when emails are sent.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                <li>
                  <Typography variant="body2">
                    <code>{'{{customerName}}'}</code> - Customer&apos;s full name
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <code>{'{{bookingDate}}'}</code> - Booking date and time
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <code>{'{{serviceName}}'}</code> - Name of the booked service
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <code>{'{{resourceName}}'}</code> - Assigned resource/staff name
                  </Typography>
                </li>
                <li>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Video size={14} />
                    <Typography variant="body2">
                      <code>{'{{meetingLink}}'}</code> - Google Meet or Zoom meeting link (if online
                      service)
                    </Typography>
                  </Box>
                </li>
              </Box>
            </CardContent>
          </Card>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              onClick={() => setIsEditing(false)}
              variant="outlined"
              sx={{
                borderRadius: theme => theme.shape.borderRadius || '8px',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={isUpdating}
              startIcon={<Check size={18} />}
              sx={{
                borderRadius: theme => theme.shape.borderRadius || '8px',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Save Changes
            </Button>
          </Box>
        </form>
      ) : (
        <>
          <Box mb={3}>
            <Box display="flex" alignItems="center">
              <Mail size={16} style={{ marginRight: '8px' }} />
              <Typography variant="body2" fontWeight={500}>
                Email Notifications:
              </Typography>
              <Chip
                size="small"
                label={emailSettings?.isEmailEnabled ? 'Active' : 'Inactive'}
                color={emailSettings?.isEmailEnabled ? 'success' : 'default'}
                sx={{ ml: 2, fontWeight: 500 }}
              />
            </Box>
          </Box>

          {!emailSettings?.isEmailEnabled ? (
            <Card
              sx={{
                borderRadius: '8px',
                backgroundColor: theme => theme.palette.background.paper,
                border: theme => `1px dashed ${theme.palette.divider}`,
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={2}
                >
                  <Mail size={48} />
                  <Typography variant="h6" color="text.secondary" mt={2}>
                    Email Notifications are Disabled
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                    Enable Email notifications to automatically keep your customers informed about
                    their appointments.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <EmailTypeCard
                  title="Schedule Emails"
                  icon={<Calendar size={20} />}
                  enabled={emailSettings?.isScheduleEmailEnabled || false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EmailTypeCard
                  title="Reschedule Emails"
                  icon={<RefreshCw size={20} />}
                  enabled={emailSettings?.isRescheduleEmailEnabled || false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EmailTypeCard
                  title="Cancellation Emails"
                  icon={<X size={20} />}
                  enabled={emailSettings?.isCancelEmailEnabled || false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EmailTypeCard
                  title="Reminder Emails"
                  icon={<Bell size={20} />}
                  enabled={emailSettings?.isReminderEmailEnabled || false}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default EmailSettings;
